import React, { useEffect, useRef } from 'react';
import { Engine } from '@babylonjs/core/Engines/engine';
import { Scene } from '@babylonjs/core/scene';
import { FreeCamera } from '@babylonjs/core/Cameras/freeCamera';
import { Vector3 } from '@babylonjs/core/Maths/math.vector';
import { Color4 } from '@babylonjs/core/Maths/math.color';
import { Color3 } from '@babylonjs/core/Maths/math.color';
import { MeshBuilder } from '@babylonjs/core/Meshes/meshBuilder';
import { StandardMaterial } from '@babylonjs/core/Materials/standardMaterial';
import { DynamicTexture } from '@babylonjs/core/Materials/Textures/dynamicTexture';
import { SolidParticleSystem } from '@babylonjs/core/Particles/solidParticleSystem';

const COUNT = 240;
const CAMERA_DIST = 60;

// Палитра искр: белые, лавандовые, янтарные, ледяные.
const PALETTE: [number, number, number][] = [
  [1, 1, 1],
  [0.72, 0.6, 1.0],
  [0.98, 0.78, 0.4],
  [0.6, 0.8, 1.0],
  [0.85, 0.65, 1.0],
];

/**
 * Полноэкранный фон из «живых» искр (Babylon SolidParticleSystem):
 * каждая искра хаотично дрейфует по собственным синусоидам, мерцает
 * и разлетается от курсора, потом медленно возвращается на место.
 * Канвас лежит под контентом (z-index:-1) и не ловит события мыши.
 */
export const SparkleField: React.FC = () => {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const engine = new Engine(canvas, true, { alpha: true, adaptToDeviceRatio: true, limitDeviceRatio: 2 });
    const scene = new Scene(engine);
    // Непрозрачный чёрный фон + CSS mix-blend-mode:screen на канвасе:
    // аддитивный блендинг не пишет альфу, и «прозрачный» канвас браузер
    // просто не показывал (цвет при нулевой альфе отбрасывается).
    scene.clearColor = new Color4(0, 0, 0, 1);
    scene.autoClear = true;

    const camera = new FreeCamera('sparkleCam', new Vector3(0, 0, -CAMERA_DIST), scene);
    camera.setTarget(Vector3.Zero());

    // Мягкая круглая точка как спрайт искры.
    const texSize = 64;
    const tex = new DynamicTexture('sparkle-tex', texSize, scene, false);
    const ctx = tex.getContext() as CanvasRenderingContext2D;
    const grad = ctx.createRadialGradient(texSize / 2, texSize / 2, 0, texSize / 2, texSize / 2, texSize / 2);
    grad.addColorStop(0, 'rgba(255,255,255,1)');
    grad.addColorStop(0.35, 'rgba(255,255,255,0.7)');
    grad.addColorStop(1, 'rgba(255,255,255,0)');
    ctx.fillStyle = grad;
    ctx.fillRect(0, 0, texSize, texSize);
    tex.hasAlpha = true;
    tex.update();

    const mat = new StandardMaterial('sparkle-mat', scene);
    mat.diffuseTexture = tex;
    mat.useAlphaFromDiffuseTexture = true;
    mat.emissiveColor = new Color3(1, 1, 1);
    mat.disableLighting = true;
    mat.alphaMode = Engine.ALPHA_ADD;
    mat.backFaceCulling = false;

    // Экранная полуширина/полувысота мира на глубине z (перспективная камера).
    const halfHeightAt = (z: number) => (CAMERA_DIST + z) * Math.tan(camera.fov / 2);

    interface SparkleData {
      base: Vector3;
      offset: { x: number; y: number };
      vel: { x: number; y: number };
      ampX: number;
      ampY: number;
      freqX: number;
      freqY: number;
      phase: number;
      twinkleFreq: number;
      twinklePhase: number;
      baseScale: number;
      // «Спутник кнопки»: кружит по эллипсу вокруг кнопки вместо свободного дрейфа.
      orbit?: { btn: number; speed: number; phase: number };
    }
    const data: SparkleData[] = [];

    // Контуры кнопок в мировых координатах (пересчитываются каждый кадр —
    // кнопки двигаются при скролле/резайзе). Кнопка — «пилюля», её контур
    // это стадион: две прямые грани длиной L и два полукруга радиуса R.
    let buttonOrbits: { cx: number; cy: number; L: number; R: number }[] = [];
    const ORBITERS_PER_BUTTON = 16;
    const refreshButtonOrbits = () => {
      const hh = halfHeightAt(0);
      const hw = hh * aspect();
      const pxToWorld = (2 * hw) / Math.max(1, window.innerWidth);
      buttonOrbits = Array.from(document.querySelectorAll('.hero__actions .btn')).map((el) => {
        const r = el.getBoundingClientRect();
        const halfH = (r.height / 2) * pxToWorld;
        const halfW = (r.width / 2) * pxToWorld;
        return {
          cx: ((r.left + r.width / 2) / window.innerWidth * 2 - 1) * hw,
          cy: -((r.top + r.height / 2) / window.innerHeight * 2 - 1) * hh,
          L: Math.max(0, 2 * (halfW - halfH)),
          R: halfH + 5 * pxToWorld, // чуть снаружи границы кнопки
        };
      });
    };

    // Точка на контуре стадиона по нормированной длине пути s ∈ [0,1).
    const stadiumPoint = (sNorm: number, L: number, R: number) => {
      const perimeter = 2 * L + 2 * Math.PI * R;
      let s = ((sNorm % 1) + 1) % 1 * perimeter;
      if (s < L) return { x: -L / 2 + s, y: R }; // верхняя грань
      s -= L;
      if (s < Math.PI * R) return { x: L / 2 + Math.sin(s / R) * R, y: Math.cos(s / R) * R }; // правый торец
      s -= Math.PI * R;
      if (s < L) return { x: L / 2 - s, y: -R }; // нижняя грань
      s -= L;
      return { x: -L / 2 - Math.sin(s / R) * R, y: -Math.cos(s / R) * R }; // левый торец
    };

    const sps = new SolidParticleSystem('sparkles', scene, { updatable: true });
    const plane = MeshBuilder.CreatePlane('sparkle-plane', { size: 1 }, scene);
    sps.addShape(plane, COUNT);
    plane.dispose();
    const mesh = sps.buildMesh();
    mesh.material = mat;
    mesh.hasVertexAlpha = true;
    sps.billboard = true;
    sps.computeParticleRotation = false;

    const aspect = () => Math.max(0.5, engine.getRenderWidth() / Math.max(1, engine.getRenderHeight()));

    sps.initParticles = () => {
      for (let i = 0; i < sps.nbParticles; i++) {
        const p = sps.particles[i];
        // Первые частицы — спутники кнопок (по ORBITERS_PER_BUTTON на кнопку).
        const isOrbiter = i < ORBITERS_PER_BUTTON * 2;
        const z = isOrbiter ? 0 : -8 + Math.random() * 34; // разброс по глубине — параллакс
        const hh = halfHeightAt(z);
        const hw = hh * aspect();
        const base = new Vector3((Math.random() * 2 - 1) * hw, (Math.random() * 2 - 1) * hh, z);
        p.position.copyFrom(base);
        const [r, g, b] = PALETTE[Math.floor(Math.random() * PALETTE.length)];
        p.color = new Color4(r, g, b, 0.85);
        data[i] = {
          base,
          offset: { x: 0, y: 0 },
          vel: { x: 0, y: 0 },
          ampX: 0.4 + Math.random() * 2.4,
          ampY: 0.4 + Math.random() * 2.4,
          freqX: 0.12 + Math.random() * 0.5,
          freqY: 0.12 + Math.random() * 0.5,
          phase: Math.random() * Math.PI * 2,
          twinkleFreq: 0.6 + Math.random() * 2.4,
          twinklePhase: Math.random() * Math.PI * 2,
          // деликатные: мелкие точки + редкие искры покрупнее
          baseScale: isOrbiter
            ? 0.08 + Math.random() * 0.12
            : Math.random() < 0.1
              ? 0.4 + Math.random() * 0.25
              : 0.1 + Math.random() * 0.18,
          orbit: isOrbiter
            ? {
                btn: i % 2,
                // Скорость — в обходах контура в секунду; половина в обратную сторону.
                speed: (0.05 + Math.random() * 0.06) * (Math.random() < 0.5 ? -1 : 1),
                // Равномерно распределяем по контуру + лёгкий джиттер.
                phase: Math.floor(i / 2) / ORBITERS_PER_BUTTON + Math.random() * 0.04,
              }
            : undefined,
        };
      }
    };

    const mouse = { x: Infinity, y: Infinity };
    const onPointerMove = (e: PointerEvent) => {
      // Курсор в мировых координатах на плоскости z=0.
      const hh = halfHeightAt(0);
      const hw = hh * aspect();
      mouse.x = (e.clientX / window.innerWidth) * 2 - 1;
      mouse.y = -((e.clientY / window.innerHeight) * 2 - 1);
      mouse.x *= hw;
      mouse.y *= hh;
    };
    window.addEventListener('pointermove', onPointerMove);

    sps.updateParticle = (p) => {
      const d = data[p.idx];
      if (!d) return p;
      const t = performance.now() * 0.001;

      // Опорная точка: эллипс вокруг кнопки (спутники) или свободный дрейф.
      const orbit = d.orbit && buttonOrbits[d.orbit.btn];
      let anchorX: number;
      let anchorY: number;
      if (orbit && d.orbit) {
        // Летим вдоль границы кнопки; R слегка «дышит», чтобы не было конвейера.
        const pt = stadiumPoint(d.orbit.phase + t * d.orbit.speed, orbit.L, orbit.R * (1 + 0.06 * Math.sin(t * 1.3 + d.phase)));
        anchorX = orbit.cx + pt.x;
        anchorY = orbit.cy + pt.y;
      } else {
        anchorX = d.base.x + Math.sin(t * d.freqX + d.phase) * d.ampX;
        anchorY = d.base.y + Math.cos(t * d.freqY + d.phase * 1.7) * d.ampY;
      }

      // Реакция на курсор: разлетаются из радиуса, скорость гасится,
      // смещение медленно стягивается обратно к «своему» месту.
      const px = anchorX + d.offset.x;
      const py = anchorY + d.offset.y;
      const dx = px - mouse.x;
      const dy = py - mouse.y;
      const dist = Math.hypot(dx, dy);
      const radius = 7;
      if (dist < radius && dist > 0.001) {
        const force = ((radius - dist) / radius) * 0.55;
        d.vel.x += (dx / dist) * force;
        d.vel.y += (dy / dist) * force;
      }
      d.offset.x = (d.offset.x + d.vel.x) * 0.985;
      d.offset.y = (d.offset.y + d.vel.y) * 0.985;
      d.vel.x *= 0.86;
      d.vel.y *= 0.86;

      p.position.x = anchorX + d.offset.x;
      p.position.y = anchorY + d.offset.y;
      p.position.z = d.base.z;

      // Мерцание: размер и прозрачность дышат несинхронно.
      const tw = 0.55 + 0.45 * Math.sin(t * d.twinkleFreq + d.twinklePhase);
      const s = d.baseScale * (0.65 + 0.35 * tw);
      p.scaling.setAll(s);
      if (p.color) p.color.a = 0.45 + 0.55 * tw;
      return p;
    };

    sps.initParticles();
    sps.setParticles();

    engine.runRenderLoop(() => {
      refreshButtonOrbits();
      sps.setParticles();
      scene.render();
    });

    const onResize = () => engine.resize();
    window.addEventListener('resize', onResize);

    return () => {
      window.removeEventListener('pointermove', onPointerMove);
      window.removeEventListener('resize', onResize);
      sps.dispose();
      scene.dispose();
      engine.dispose();
    };
  }, []);

  return <canvas ref={canvasRef} className="sparkles" aria-hidden="true" />;
};
